import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  shareFolder, fetchSharedMembers,
  removeSharedMember, updateSharedMemberPermission,
} from '../api';
import { getSession } from '../api';

const {
  FiX, FiShare2, FiMail, FiUserMinus, FiCheck,
  FiLoader, FiUsers, FiEdit2, FiEye, FiLogOut,
} = FiIcons;

const PERMISSION_CONFIG = {
  viewer: { label: 'Viewer', icon: FiEye,   color: '#a78bfa', desc: 'Can view & open bookmarks' },
  editor: { label: 'Editor', icon: FiEdit2, color: '#34d399', desc: 'Can add, edit & delete inside' },
};

function PermissionBadge({ permission, small }) {
  const cfg = PERMISSION_CONFIG[permission] || PERMISSION_CONFIG.viewer;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${small ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5'}`}
      style={{ backgroundColor: `${cfg.color}20`, color: cfg.color, border: `1px solid ${cfg.color}44` }}>
      <SafeIcon icon={cfg.icon} className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

export default function ShareFolderModal({ isOpen, onClose, folder, themeColors: tc, onLeaveFolder, isDark: isDarkProp }) {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('viewer');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const user = getSession();
  const isDark = isDarkProp !== undefined
    ? Boolean(isDarkProp)
    : !tc?.card?.toLowerCase().includes('ffffff') && !tc?.card?.startsWith('rgba(255');
  const inputBg = isDark ? 'rgba(0,0,0,0.35)' : '#FFFFFF';
  const inputBorder = isDark ? tc?.border : '#CBD5E1';

  // Is this a shared folder we're viewing as a member (not owner)?
  const isSharedFolder = folder?._isShared;

  useEffect(() => {
    if (isOpen && folder) {
      loadMembers();
      setEmail('');
      setPermission('viewer');
      setError('');
      setSuccess('');
    }
  }, [isOpen, folder]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const fid = folder._realFolderId || folder._dbFolderId || folder.targetId || folder.id;
      const m = await fetchSharedMembers(fid);
      setMembers(m);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    if (trimmed === user?.email) {
      setError("You can't share a folder with yourself.");
      return;
    }
    setSharing(true);
    setError('');
    setSuccess('');
    try {
      const folderId = folder._realFolderId || folder._dbFolderId || folder.targetId || folder.id;
      await shareFolder({
        folderId,
        folderTitle: folder.title,
        ownerEmail: user.email,
        ownerId: user.id,
        inviteeEmail: trimmed,
        permission,
      });
      setSuccess(`Shared with ${trimmed} as ${permission}!`);
      setEmail('');
      await loadMembers();
    } catch (err) {
      setError(err.message || 'Failed to share. Try again.');
    } finally {
      setSharing(false);
    }
  };

  const handleRemove = async (member) => {
    try {
      await removeSharedMember(member.id);
      await loadMembers();
    } catch (e) {
      setError('Failed to remove member.');
    }
  };

  const handlePermissionToggle = async (member) => {
    const newPerm = member.permission === 'editor' ? 'viewer' : 'editor';
    setUpdatingId(member.id);
    try {
      await updateSharedMemberPermission(member.id, newPerm);
      await loadMembers();
    } catch (e) {
      setError('Failed to update permission.');
    } finally {
      setUpdatingId(null);
    }
  };

  // Member leaving a shared folder they were invited to
  const handleLeave = async () => {
    if (!folder._memberRecordId) return;
    try {
      await removeSharedMember(folder._memberRecordId);
      onClose();
      if (onLeaveFolder) onLeaveFolder(folder.id);
    } catch (e) {
      setError('Failed to leave folder.');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]" />
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 16 }}
            transition={{ type: 'spring', stiffness: 420, damping: 30 }}
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden pointer-events-auto"
              style={{ backgroundColor: tc.card, border: `1px solid ${tc.border}` }}>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: tc.border }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${tc.accent}22` }}>
                    <SafeIcon icon={FiShare2} className="w-4 h-4" style={{ color: tc.accent }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: tc.text }}>
                      {isSharedFolder ? 'Shared Folder' : 'Share Folder'}
                    </p>
                    <p className="text-[11px] truncate max-w-[200px]" style={{ color: tc.muted }}>
                      {folder?.title}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: tc.muted }}>
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-5">

                {/* If member (not owner): show permission info + Leave button */}
                {isSharedFolder ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-xl"
                      style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', border: `1px solid ${tc.border}` }}>
                      <div>
                        <p className="text-xs font-medium mb-1" style={{ color: tc.text }}>Your access level</p>
                        <PermissionBadge permission={folder?._sharedPermission || 'viewer'} />
                      </div>
                      <div className="text-right">
                        <p className="text-[10px]" style={{ color: tc.muted }}>
                          {PERMISSION_CONFIG[folder?._sharedPermission || 'viewer']?.desc}
                        </p>
                      </div>
                    </div>

                    <button onClick={handleLeave}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all"
                      style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.2)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.12)')}>
                      <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                      Leave this shared folder
                    </button>

                    {error && <p className="text-xs text-red-400">{error}</p>}
                  </div>
                ) : (
                  <>
                    {/* Owner: Invite form */}
                    <form onSubmit={handleShare} className="space-y-3">
                      <label className="block text-xs font-medium" style={{ color: tc.muted }}>
                        Invite by email
                      </label>
                      <div className="relative">
                        <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                          style={{ color: tc.muted }} />
                        <input
                          type="email" value={email}
                          onChange={(e) => { setEmail(e.target.value); setError(''); setSuccess(''); }}
                          placeholder="user@example.com"
                          className="w-full border rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none"
                          style={{ backgroundColor: inputBg, borderColor: inputBorder, color: tc.text }}
                          onFocus={(e) => (e.currentTarget.style.borderColor = tc.accent)}
                          onBlur={(e) => (e.currentTarget.style.borderColor = inputBorder)}
                        />
                      </div>

                      {/* Permission selector */}
                      <div className="flex gap-2">
                        {Object.entries(PERMISSION_CONFIG).map(([key, cfg]) => (
                          <button key={key} type="button"
                            onClick={() => setPermission(key)}
                            className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all border"
                            style={{
                              backgroundColor: permission === key ? `${cfg.color}18` : 'transparent',
                              borderColor: permission === key ? `${cfg.color}66` : tc.border,
                              color: permission === key ? cfg.color : tc.muted,
                            }}>
                            <SafeIcon icon={cfg.icon} className="w-3.5 h-3.5" />
                            <div className="text-left">
                              <div>{cfg.label}</div>
                              <div className="text-[9px] opacity-70">{cfg.desc}</div>
                            </div>
                          </button>
                        ))}
                      </div>

                      <button type="submit" disabled={sharing || !email.trim()}
                        className="w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all"
                        style={{
                          backgroundColor: email.trim() && !sharing ? tc.accent : `${tc.accent}44`,
                          color: '#000',
                        }}>
                        {sharing
                          ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                          : <SafeIcon icon={FiShare2} className="w-4 h-4" />}
                        Invite as {PERMISSION_CONFIG[permission]?.label}
                      </button>

                      <AnimatePresence>
                        {error && (
                          <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            className="text-xs text-red-400">{error}</motion.p>
                        )}
                        {success && (
                          <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            className="text-xs flex items-center gap-1" style={{ color: tc.accent }}>
                            <SafeIcon icon={FiCheck} className="w-3 h-3" /> {success}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </form>

                    {/* Members list */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" style={{ color: tc.muted }} />
                        <span className="text-xs font-medium" style={{ color: tc.muted }}>
                          Shared with ({members.length})
                        </span>
                      </div>

                      {loading ? (
                        <div className="flex justify-center py-6">
                          <div className="w-5 h-5 rounded-full border-2 animate-spin"
                            style={{ borderColor: `${tc.accent}44`, borderTopColor: tc.accent }} />
                        </div>
                      ) : members.length === 0 ? (
                        <div className="text-center py-6 text-xs" style={{ color: tc.muted }}>
                          Not shared with anyone yet.
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
                          {members.map((m) => (
                            <motion.div key={m.id} layout
                              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                              className="flex items-center justify-between px-3 py-2.5 rounded-xl"
                              style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', border: `1px solid ${tc.border}` }}>
                              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${tc.accent}20` }}>
                                  <SafeIcon icon={FiMail} className="w-3 h-3" style={{ color: tc.accent }} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-medium truncate" style={{ color: tc.text }}>
                                    {m.invitee_email}
                                  </p>
                                  <PermissionBadge permission={m.permission || 'viewer'} small />
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {/* Toggle permission */}
                                <button onClick={() => handlePermissionToggle(m)}
                                  disabled={updatingId === m.id}
                                  className="p-1.5 rounded-lg transition-colors text-[10px] font-medium px-2"
                                  style={{ color: tc.muted, border: `1px solid ${tc.border}` }}
                                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = tc.accent)}
                                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = tc.border)}
                                  title={`Switch to ${m.permission === 'editor' ? 'viewer' : 'editor'}`}>
                                  {updatingId === m.id
                                    ? <SafeIcon icon={FiLoader} className="w-3 h-3 animate-spin" />
                                    : <SafeIcon icon={m.permission === 'editor' ? FiEye : FiEdit2} className="w-3 h-3" />}
                                </button>
                                {/* Remove */}
                                <button onClick={() => handleRemove(m)}
                                  className="p-1.5 rounded-lg transition-colors"
                                  style={{ color: tc.muted }}
                                  onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                                  onMouseLeave={(e) => (e.currentTarget.style.color = tc.muted)}
                                  title="Remove access">
                                  <SafeIcon icon={FiUserMinus} className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}